// src/services/config/firebase.js - Production Ready Configuration
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';

// Your Firebase config object - REPLACE WITH YOUR ACTUAL CONFIG
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validate Firebase configuration
const validateFirebaseConfig = () => {
  const requiredFields = ['apiKey', 'authDomain', 'projectId'];
  const missingFields = requiredFields.filter(field => 
    !firebaseConfig[field] || firebaseConfig[field].includes('your-'),
  );
  
  if (missingFields.length > 0) {
    console.warn('⚠️ Firebase config incomplete. Missing or placeholder values for:', missingFields);
    console.warn('📝 Please update src/services/config/firebase.js with your actual Firebase project credentials.');
    return false;
  }
  
  return true;
};

// Initialize Firebase only if config is valid
let app = null;
let auth = null;
const analytics = null;
const performance = null;
const remoteConfig = null;

try {
  if (validateFirebaseConfig()) {
    console.log('🔥 Initializing Firebase...');
    
    // Initialize Firebase
    app = initializeApp(firebaseConfig);
    
    // Initialize Auth
    auth = getAuth(app);
    
    // No Firestore. Everything a visitor asks us to keep goes to the auth hub
    // (HubSyncService: getAppState / saveAppState), so there is one place to
    // look for it and one place to delete it. This project's own Firestore was
    // initialised here and never read or written.
    
    // No Firebase Analytics, Performance or Remote Config here. Until
    // September 2026 this file started Analytics and Performance on every
    // production page load whenever a measurement ID was set, with no
    // consent. Usage analytics now goes through the fleet's consent rail
    // (services/analytics.js, @ddd/analytics): nothing is sent until the
    // visitor says yes. Remote Config was initialised and never read.

    // Connect to emulators in development (optional)
    if (import.meta.env.MODE === 'development' && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
      console.log('🔧 Connecting to Firebase emulators...');
      
      try {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        console.log('✅ Connected to Firebase emulators');
      } catch (emulatorError) {
        console.warn('⚠️ Emulator connection failed:', emulatorError.message);
      }
    }
    
    console.log('✅ Firebase initialized successfully');
  } else {
    console.log('⚠️ Firebase initialization skipped due to incomplete configuration');
  }
} catch (error) {
  console.error('❌ Firebase initialization failed:', error);
  
  // Create mock objects to prevent app crashes
  auth = {
    currentUser: null,
    onAuthStateChanged: (callback) => callback(null),
    signOut: () => Promise.resolve(),
    signInAnonymously: () => Promise.reject(new Error('Firebase not configured')),
    signInWithEmailAndPassword: () => Promise.reject(new Error('Firebase not configured')),
    createUserWithEmailAndPassword: () => Promise.reject(new Error('Firebase not configured')),
  };
  
}

// Helper function to check if Firebase is properly configured
export const isFirebaseConfigured = () => {
  return app !== null && validateFirebaseConfig();
};

// Helper function to get connection status
export const getFirebaseStatus = () => {
  return {
    configured: isFirebaseConfigured(),
    hasAuth: !!auth,
    hasAnalytics: !!analytics,
    hasPerformance: !!performance,
    environment: import.meta.env.MODE,
  };
};

// Export Firebase services
// ---------------------------------------------------------------------------
// The DDD auth hub, as a SECOND Firebase app.
//
// It MUST be a named app ('auth-hub'). Sharing the default app would make the
// hub session look like this project's session, and every visitor would appear
// signed in. Without VITE_AUTH_HUB_API_KEY in the build there is no hub app at
// all, and HubSessionService turns sync off rather than guessing.
// ---------------------------------------------------------------------------
const authHubConfig = {
  apiKey: import.meta.env.VITE_AUTH_HUB_API_KEY,
  authDomain: 'ddd-auth-hub-4ed14.firebaseapp.com',
  projectId: 'ddd-auth-hub-4ed14',
};

let authHubApp = null;
let authHubAuth = null;

if (authHubConfig.apiKey) {
  try {
    authHubApp = initializeApp(authHubConfig, 'auth-hub');
    authHubAuth = getAuth(authHubApp);
  } catch (error) {
    console.error('Auth hub initialisation error:', error);
  }
}

export { authHubApp, authHubAuth };

export { app, auth, analytics, performance, remoteConfig };

// Export default auth for backward compatibility
export default auth;